// Google Analytics 4 (GA4) Tracking Utility
// Measurement ID: G-4RELW0W0JR

const GA4_MEASUREMENT_ID = 'G-4RELW0W0JR';

/**
 * Initialize GA4 tracking
 * Should be called once when the app loads
 */
export const initGA4 = () => {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('GA4: gtag is not available. Ensure the script is loaded in index.html');
    return;
  }

  console.log('GA4: Initialized successfully with measurement ID:', GA4_MEASUREMENT_ID);
};

/**
 * Track page view
 * Should be called on route changes
 * @param {string} path - The URL path to track
 * @param {string} title - The page title
 */
export const trackPageView = (path, title = document.title) => {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('GA4: gtag is not available for page view tracking');
    return;
  }

  window.gtag('config', GA4_MEASUREMENT_ID, {
    page_path: path,
    page_title: title,
  });

  console.log('GA4: Page view tracked -', path, title);
};

/**
 * Track custom events
 * @param {string} eventName - Name of the event
 * @param {Object} eventParams - Additional event parameters
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('GA4: gtag is not available for event tracking');
    return;
  }

  window.gtag('event', eventName, eventParams);
  
  console.log('GA4: Event tracked -', eventName, eventParams);
};

/**
 * Track user engagement events
 */
export const trackUserEngagement = {
  // Button clicks
  buttonClick: (buttonName, location = 'unknown') => {
    trackEvent('button_click', {
      button_name: buttonName,
      location: location,
    });
  },

  // Form submissions
  formSubmit: (formName, success = true) => {
    trackEvent('form_submit', {
      form_name: formName,
      success: success,
    });
  },

  // Link clicks
  linkClick: (linkName, url) => {
    trackEvent('link_click', {
      link_name: linkName,
      url: url,
    });
  },

  // Scroll depth
  scrollDepth: (percentage) => {
    trackEvent('scroll_depth', {
      percentage: percentage,
    });
  },

  // Time on page
  timeOnPage: (seconds) => {
    trackEvent('time_on_page', {
      seconds: seconds,
    });
  },

  // Video interactions
  videoPlay: (videoTitle) => {
    trackEvent('video_play', {
      video_title: videoTitle,
    });
  },

  videoPause: (videoTitle) => {
    trackEvent('video_pause', {
      video_title: videoTitle,
    });
  },

  // Calculator interactions
  calculatorUse: (calculatorName) => {
    trackEvent('calculator_use', {
      calculator_name: calculatorName,
    });
  },

  // File downloads
  fileDownload: (fileName, fileType) => {
    trackEvent('file_download', {
      file_name: fileName,
      file_type: fileType,
    });
  },
};

/**
 * Track errors
 * @param {string} errorDescription - Description of the error
 * @param {boolean} fatal - Whether the error was fatal
 */
export const trackError = (errorDescription, fatal = false) => {
  trackEvent('exception', {
    description: errorDescription,
    fatal: fatal,
  });
};

/**
 * Track user properties
 * @param {Object} userProperties - User properties to set
 */
export const setUserProperties = (userProperties) => {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('GA4: gtag is not available for setting user properties');
    return;
  }

  Object.keys(userProperties).forEach((property) => {
    window.gtag('set', 'user_properties', {
      [property]: userProperties[property],
    });
  });

  console.log('GA4: User properties set', userProperties);
};

/**
 * Enable enhanced measurement features
 */
export const enableEnhancedMeasurement = () => {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('GA4: gtag is not available');
    return;
  }

  // Enhanced measurement is automatically enabled in GA4 admin
  // This function serves as a reminder to verify settings in GA4 dashboard
  console.log('GA4: Enhanced measurement should be enabled in GA4 admin panel');
};

export default {
  initGA4,
  trackPageView,
  trackEvent,
  trackUserEngagement,
  trackError,
  setUserProperties,
  enableEnhancedMeasurement,
  GA4_MEASUREMENT_ID,
};
